import { useState, useEffect, useRef } from "react";
import { useShowcasePhotos } from "@/hooks/useShowcasePhotos";

/**
 * ShowcaseHeroBackground
 * Full-bleed cross-fading background collage for hero sections.
 * Sits behind other content; heavily darkened + edge-vignetted so text stays readable.
 *
 * Renders nothing if admin hasn't uploaded any photos — the base UI still looks right.
 */
export function ShowcaseHeroBackground({ intervalMs = 7000 }) {
  const photos = useShowcasePhotos();
  const [idx, setIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (photos.length < 2) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => {
        setPrevIdx(i);
        // Pick a random next photo that's not the current one
        let next = Math.floor(Math.random() * photos.length);
        if (next === i) next = (next + 1) % photos.length;
        return next;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [photos.length, intervalMs]);

  if (photos.length === 0) return null;

  const current = photos[idx % photos.length];
  const previous = prevIdx != null ? photos[prevIdx % photos.length] : null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="showcase-hero-bg">
      {/* Outgoing image — fades from 35% → 0 */}
      {previous && (
        <img
          key={`prev-${previous.id}`}
          src={previous.image_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: "showcaseHeroOut 1.6s ease-in-out forwards", opacity: 0.35 }}
        />
      )}
      {/* Incoming image — fades from 0 → 35% and stays there */}
      <img
        key={`cur-${current.id}`}
        src={current.image_url}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: "showcaseHeroIn 1.6s ease-in-out forwards", opacity: 0.35 }}
      />
      {/* Vignette + darkening — keeps text on top readable but photos remain visible */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse at center, rgba(5,5,5,0.30) 0%, rgba(5,5,5,0.60) 60%, rgba(5,5,5,0.88) 100%)"
      }} />
      {/* Extra top/bottom fades so it blends into surrounding sections */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#050505] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#050505] to-transparent" />
    </div>
  );
}
